import React, {
  useRef,
  createContext,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import 'twin.macro';
import tw from 'twin.macro';
import { uniqueId } from 'lodash';
import { AdCreativeType, AdPlacement } from 'types/adTypes';
import { CustomerResource } from 'types/apiTypes';
import { ApolloError, useApolloClient } from '@apollo/client';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { Controller, useForm } from 'react-hook-form';

import { useMutation } from 'hooks/sympl-mutation';
import {
  COPY_VIDEO,
  GENERATE_VIDEO,
  UPDATE_VIDEO,
} from 'graphql/ad-variants/mutations';
import useNavigationContext from 'hooks/context/nav-context';
import useAdEditorContext from 'hooks/context/ad-editor-context';
import { AdEditorSideBarTabs } from 'views/ad-builder/AdBuilder';
import {
  readPictureRefusal,
  describePictureRefusal,
} from 'components/video-gen/pictureRefusal';
import Button from 'components/button/Button';
import SpinningSymplLoader from 'components/loading/spinningSymplLoader';
import { Footnote } from 'components/typography/Typography';
import { fireEvent } from 'utils/eventHelper';
import Input from 'components/form/input/Input';

import GenerateVideoModal from 'components/modals/GenerateVideoModal';
import VideoPreviewModal from 'components/modals/VideoPreviewModal';
import { VideoWizard } from 'components/video-gen';
import { WIZARD_OPENS_ON } from 'components/video-gen/VideoWizard';
import { VideoScene, VideoTemplateStyle } from 'components/video-gen/types';
import {
  buildUpdatePayload,
  detectScenesEdited,
  VideoWideChoices,
} from 'components/video-gen/updatePayload';
import { parsePreviewPayload } from 'components/video-gen/parsePreviewPayload';
import { StylePaces } from 'components/video-gen/stylePace';
import { withWhatTheDraftHolds } from 'components/video-gen/storedCrops';
import {
  bringToLifeMarkFrom,
  CANNOT_EDIT_BRING_TO_LIFE,
  CANNOT_REOPEN,
  carriesBringToLifeClip,
  wizardEntryFor,
} from 'components/video-gen/wizardEntry';
import { waitForServerVideoAd } from 'components/video-gen/serverVideoAd';
import { GET_EDITOR_DATA } from 'graphql/vacancies/queries';
import { getLocalAppSession } from 'utils/storageHelpers';
import { AdVariant } from 'types/adEditorTypes';
import {
  DEFAULT_LOGO_POSITION,
  LogoPosition,
  logoPositionFrom,
} from 'components/video-gen/advancedScenes';

import {
  fetchPreviewConfig,
  reopenVideo,
  generateVideo,
  generateVideoFal,
  updateVideo,
  FAL_MOCK_MODE,
  PreviewConfig,
  PreviewConfigResponse,
  PreviewPlan,
  RenderedResource,
  BtlWordKey,
} from 'api/video';
import { useVideoPolling } from 'hooks/useVideoPolling';
import { useStyleExamples } from 'hooks/useStyleExamples';
import {
  BTL_OBSERVED_DURATION_MS,
  BTL_POLLING_CEILING_MS,
} from 'config/videoGeneration';
import { useVideoGenerationLocks } from 'hooks/useVideoGenerationLocks';
import {
  VideoGenerationLocks,
  VIDEO_LOCKED_MESSAGE,
} from 'utils/videoGenerationLocks';
import BringToLifeModal from 'components/video-gen/BringToLifeModal';
import ResourcesModal from 'components/resources-modal/ResourcesModal';
import { isEmptyUnsavedVariant } from 'components/video-gen/emptyVariant';
import {
  BtlRefusal,
  cropCanFixIt,
  readBtlRefusal,
} from 'components/video-gen/btlRefusal';
import { getBtlCrop } from 'components/video-gen/btlSource';
import {
  BtlChoices,
  btlOverlayFields,
  readWordRefusal,
} from 'components/video-gen/btlWords';
import { bringToLifeWillSharpen } from 'utils/sharpening';
import { useToastNotifications } from 'hooks/notificationHooks';
import { ToastTypes } from 'types/notificationTypes';
import { useBtlFailureAnnouncer } from 'hooks/useBtlFailureAnnouncer';

// ---------------------------------------------------------------------------
// Context type
// ---------------------------------------------------------------------------

/**
 * SYM-6220. The look a new video starts with when the server names none.
 *
 * The real default is the server's: a new draft is created with a style
 * written into its config, and this only covers a reply that carries none.
 * Keep the two the same. Dynamic, because it is the look that carries the
 * slow drift across the photograph (SYM-6221) and is the one a customer
 * should meet first.
 */
const DEFAULT_TEMPLATE_STYLE: VideoTemplateStyle = 'dynamic';

type VideoGenContextType = {
  videoGen: () => void;
  copyVideo: () => void;
  bringToLife: () => void;
  isAnyVideoRendering: boolean;
  /** SYM-5391. Which video button is blocked on this vacancy right now; the components hide it. */
  videoLocks: VideoGenerationLocks;
};

export const VideoGenContext = createContext<VideoGenContextType>(
  {} as VideoGenContextType
);

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

const VideoGenProvider: React.FC<ReactNode> = ({ children }) => {
  const { activeVacancy, currentVacancy } = useNavigationContext();
  const {
    updateCurrentVariant,
    currentVariant,
    variants,
    addVariant,
    deleteCurrentVariant,
    setActiveTab,
    setUploadMode,
    setBtlCropMinimum,
  } = useAdEditorContext();
  // SYM-5391. Server-held per-provider locks, mirrored here and across tabs.
  const {
    locks: videoLocks,
    refetch: refetchVideoLocks,
    setLocked: setVideoLocked,
  } = useVideoGenerationLocks(activeVacancy);
  const { addToast } = useToastNotifications();

  // Track 1 routing flag. When false (and user is here via the ai-video
  // fallback), `videoGen` is bound to the legacy GraphQL flow below.
  // LEGACY: drop this hook + the legacy block below once ai-video is retired.
  const hasT1 = useFeatureFlagEnabled('video-track-1-scenario');

  // ── Modal visibility ──────────────────────────────────────────────────────
  const [wizardOpen, setWizardOpen] = useState(false);
  /** SYM-6197. Open while the customer picks pictures at the refusal. */
  const [picturePickerOpen, setPicturePickerOpen] = useState(false);
  const [legacyModalOpen, setLegacyModalOpen] = useState(false);
  const [videoGenError, setVideoGenError] = useState<string[] | null>(null);
  /* SYM-6181. A refusal we can read gets the action that fixes it; anything
     else is shown as it arrived. */
  const pictureRefusal = useMemo(
    () => readPictureRefusal(videoGenError),
    [videoGenError]
  );

  // ── Preview config state (T1) ────────────────────────────────────────────
  const [previewLoading, setPreviewLoading] = useState(false);
  const [draftId, setDraftId] = useState<number | null>(null);
  const [maxSlideCharacters, setMaxSlideCharacters] = useState<number | null>(
    null
  );
  // SYM-6215. The pace each style stands for, from the same place as the
  // character budget. Picking a style applies it to the untouched slides.
  const [stylePaces, setStylePaces] = useState<StylePaces | null>(null);
  const [previewConfig, setPreviewConfig] = useState<PreviewConfig | null>(
    null
  );
  const [previewPayload, setPreviewPayload] = useState<Record<
    string,
    any
  > | null>(null);
  /**
   * SYM-6206. The plan for a video OUR renderer will make: the same pictures
   * ffmpeg composites, with their positions and timings, for the wizard to
   * play. Null for a Creatomate draft, which keeps the composition above.
   *
   * Both are held at once on purpose. Which renderer a draft belongs to is the
   * server's business, and it answers by sending one or the other; the wizard
   * plays whichever it was given rather than deciding for itself.
   */
  const [previewPlan, setPreviewPlan] = useState<PreviewPlan | null>(null);
  const [initialScenes, setInitialScenes] = useState<VideoScene[]>([]);
  // SYM-6265. Where the draft says the logo sits; the baseline edits are
  // measured against, like the style next to it.
  const [initialLogoPosition, setInitialLogoPosition] = useState<LogoPosition>(
    DEFAULT_LOGO_POSITION
  );
  const [initialStyle, setInitialStyle] = useState<VideoTemplateStyle>(
    DEFAULT_TEMPLATE_STYLE
  );

  /**
   * SYM-6226. The three moving examples the Style step shows.
   *
   * Asked for HERE rather than in the step, and that is the whole reason it is
   * here: the Style step is mounted and thrown away every time the customer
   * moves between steps, so asking there would re-run a person detector over
   * the draft's photographs on every visit for a picture that cannot have
   * moved. This asks once per draft and holds the answer for as long as the
   * draft lives. It costs one request for a customer who never opens Style,
   * which is the trade the hook's own comment argues.
   */
  const { state: styleExamples, retry: retryStyleExamples } = useStyleExamples(
    draftId,
    wizardOpen
  );

  // ── The finished video's ad (SYM-6367) ───────────────────────────────────
  //
  // The server turns every finished video into an ad by itself. The builder
  // used to add its own copy too, and Save stored that copy as a second ad, so
  // every video showed twice. Now it only shows the ad the server made. See
  // serverVideoAd.ts.
  const apolloClient = useApolloClient();
  // Read at the moment the ad arrives, seconds after the call began.
  const variantsRef = useRef(variants);
  variantsRef.current = variants;
  // Videos already being shown, by file. A second "finished" for the same
  // video arrives before the list re-renders, so the list cannot answer.
  const shownVideoFilesRef = useRef(new Set<number>());

  const showServerVideoAd = useCallback(
    // true: shown. false: never appeared. undefined: already being handled.
    async (videoFileId: number): Promise<boolean | undefined> => {
      if (!activeVacancy) return false;
      if (shownVideoFilesRef.current.has(videoFileId)) return undefined;
      shownVideoFilesRef.current.add(videoFileId);
      const ad = await waitForServerVideoAd<AdVariant>(async () => {
        // 'no-cache' on purpose: an answer written to the cache would reach
        // the builder's own copy of this query, which rebuilds the whole list
        // and would throw away the customer's unsaved edits on other ads.
        const { data } = await apolloClient.query<
          { editorData: { adVariants: AdVariant[] } },
          { vacancy: number }
        >({
          query: GET_EDITOR_DATA,
          variables: { vacancy: activeVacancy },
          fetchPolicy: 'no-cache',
          // The header hooks/sympl-query adds to every request, which a direct
          // query bypasses. Without it every attempt answered 400 on QA (30 Sep
          // 2026, job 25874) -- the same trap AdBuilder's refetch documents.
          context: {
            headers: {
              'Active-Customer': getLocalAppSession()?.activeCustomer,
            },
          },
        });
        return data?.editorData?.adVariants ?? [];
      }, videoFileId);

      if (!ad) {
        shownVideoFilesRef.current.delete(videoFileId);
        return false;
      }
      // The list may have been reloaded meanwhile and hold it already.
      if (variantsRef.current.some(({ id }) => id === ad.id)) return true;

      addVariant({
        ...ad,
        bring_to_life_resource_id: bringToLifeMarkFrom(ad),
        isDirty: false,
        isDeleted: false,
        company: ad.company ?? currentVacancy?.brand?.name,
        uuid: uniqueId(),
      });
      return true;
    },
    [activeVacancy, apolloClient, addVariant, currentVacancy]
  );

  const announceLateVideoAd = useCallback(() => {
    addToast({
      type: ToastTypes.INFO,
      title: 'Your video is ready',
      description:
        'It is being added to your ads. Refresh the page in a minute to see it.',
    });
  }, [addToast]);

  // ── Generation polling (T1) ──────────────────────────────────────────────
  const [generationId, setGenerationId] = useState<number | null>(null);
  const [isRendering, setIsRendering] = useState(false);

  const handlePollingCompleted = useCallback(
    (resource: RenderedResource | null) => {
      setIsRendering(false);
      setWizardOpen(false);
      if (!resource) return;
      // SYM-6367. The video is NOT put on the ad it was started from any
      // more: the server makes a new ad for it, and doing both left two. A
      // blank ad made with the plus button only to start the video is cleared
      // away, as when the video is abandoned (SYM-6197); an ad with a picture
      // keeps it.
      if (isEmptyUnsavedVariant(currentVariant)) deleteCurrentVariant();
      showServerVideoAd(resource.id).then((shown) => {
        if (shown === false) announceLateVideoAd();
      });
    },
    [
      currentVariant,
      deleteCurrentVariant,
      showServerVideoAd,
      announceLateVideoAd,
    ]
  );

  const handlePollingError = useCallback((message: string) => {
    setIsRendering(false);
    setVideoGenError([message]);
  }, []);

  const { progress } = useVideoPolling({
    generationId: isRendering ? generationId : null,
    onCompleted: handlePollingCompleted,
    onError: handlePollingError,
    // SYM-6340. The bar runs on the time 9 renders in 10 take: 43 s, measured
    // on QA 24 Sep 2026 over 69 renders by our own renderer (worker log,
    // median 25 s, slowest 2 min 33 s). The point where the page stops waiting
    // stays where the old 90 s default put it (2.5 x 90 s): derived from 43 s
    // it would be 1 min 48 s, under the slowest render actually seen.
    expectedDurationMs: 43_000,
    maxDurationMs: 225_000,
  });

  // ── Track 2 — Bring to life (Hailuo) ─────────────────────────────────────
  const [btlOpen, setBtlOpen] = useState(false);
  const [btlStage, setBtlStage] = useState<
    | 'input'
    | 'generating'
    | 'still-working'
    | 'done'
    | 'error'
    | 'picture-too-small'
  >('input');
  const [btlGenerationId, setBtlGenerationId] = useState<number | null>(null);
  const [btlSubmitError, setBtlSubmitError] = useState<string | null>(null);
  // SYM-6073. Why the picture was refused, and what they had typed when it was.
  // Both survive the trip to the crop tool and back.
  const [btlRefusal, setBtlRefusal] = useState<BtlRefusal | null>(null);
  // SYM-6135. The server sharpens a too-small picture inside the request,
  // which adds about 15 seconds; the modal says so for as long as it runs.
  const [btlSharpening, setBtlSharpening] = useState(false);
  // What they typed and chose, kept while they fix the crop or the picture.
  // SYM-6353: the look, the logo and the words as well as the instructions.
  const [btlChoicesDraft, setBtlChoicesDraft] =
    useState<Partial<BtlChoices> | null>(null);
  const [btlKey, setBtlKey] = useState(0);
  // SYM-6353. The server's refusal of a word, shown under that word.
  const [btlWordErrors, setBtlWordErrors] = useState<
    Partial<Record<BtlWordKey, string>>
  >({});

  const handleBtlCompleted = useCallback(
    (resource: RenderedResource | null) => {
      setBtlStage('done');
      setBtlOpen(false);
      setVideoLocked('fal', false); // SYM-5391
      if (!resource) return;
      // SYM-6367. The server makes the clip's ad; the builder used to add its
      // own unsaved copy as well, and Save stored it as a second ad. The
      // server's ad carries its own Bring to Life mark (SYM-6185).
      showServerVideoAd(resource.id).then((shown) => {
        if (shown === false) {
          announceLateVideoAd();
          return;
        }
        if (shown === undefined) return;
        addToast({
          type: ToastTypes.SUCCESS,
          title: 'Your video clip is ready',
          description: 'The animated variant has been added to your ad.',
        });
      });
    },
    [showServerVideoAd, announceLateVideoAd, addToast]
  );

  // SYM-6337. After "Close and wait" the error screen is gone, so a failure is
  // announced with a toast instead (see the hook for why it reads the window's
  // state at the moment of the failure).
  const announceBtlFailure = useBtlFailureAnnouncer(btlOpen, addToast);

  const handleBtlError = useCallback(
    (reason: string) => {
      setBtlStage('error');
      setVideoLocked('fal', false); // SYM-5391: the server released it with the failure
      announceBtlFailure(reason);
    },
    [announceBtlFailure]
  );

  // The browser stopped waiting. The generation has NOT failed: it runs
  // server-side, finishes on its own, and attaches itself to the ad. Saying
  // "something went wrong" here is a lie, and telling the customer to retry is
  // worse, because the retry hits the per-vacancy lock the live generation
  // still holds. See SYM-5858.
  const handleBtlStillWorking = useCallback(() => {
    setBtlStage('still-working');
  }, []);

  const { progress: btlProgress } = useVideoPolling({
    /**
     * SYM-6075. Hand the hook the id for as long as a generation is IN FLIGHT,
     * not only while the customer is being made to wait.
     *
     * `still-working` used to be excluded here, and that single omission
     * switched off the very watch that screen promises is still running:
     * reaching the deadline sets the stage, the id goes null in the same tick,
     * the polling effect tears itself down, and the finished clip has no way
     * back into the page. Measured 2026-08-28 — `deadline-reached` and
     * `effect-cleanup` land in the same second, with no request after it.
     */
    generationId:
      btlStage === 'generating' || btlStage === 'still-working'
        ? btlGenerationId
        : null,
    onCompleted: handleBtlCompleted,
    onError: handleBtlError,
    onTimeout: handleBtlStillWorking,
    expectedDurationMs: BTL_OBSERVED_DURATION_MS,
    maxDurationMs: BTL_POLLING_CEILING_MS,
    isMock: FAL_MOCK_MODE,
  });

  const bringToLife = useCallback(async () => {
    setBtlStage('input');
    setBtlGenerationId(null);
    setBtlSubmitError(null);
    setBtlWordErrors({});
    setBtlRefusal(null);
    setBtlCropMinimum(null);
    // Remounts the modal, which reseeds the screen from btlChoicesDraft --
    // empty on a normal open, and what they had typed when they are coming
    // back from widening the crop.
    setBtlKey((k) => k + 1);
    setBtlOpen(true);
    // SYM-5391. The button is hidden while locked, but a tab open for a while may not know: ask
    // the server on the way in, and say so on the form rather than after a refused submit.
    const fresh = await refetchVideoLocks();
    if (fresh.fal) setBtlSubmitError(VIDEO_LOCKED_MESSAGE);
  }, [refetchVideoLocks]);

  /**
   * Close the modal and put the crop tool in front of them. SYM-6073.
   *
   * The crop box is not part of Bring to Life: it belongs to the ad editor's
   * right-hand panel, under "Resize", and that panel may not even be on the
   * right tab. Telling someone to widen a crop while leaving them to find it is
   * most of the annoyance the old message caused.
   */
  const adjustCropForBtl = useCallback(
    (choices: BtlChoices) => {
      setBtlChoicesDraft(choices);
      setBtlOpen(false);
      setActiveTab(AdEditorSideBarTabs.VISUAL_OPTIONS);

      // The panel has to render before there is anything to scroll to. A frame
      // is not always enough when the tab is being switched at the same time.
      window.setTimeout(() => {
        document
          .getElementById('ad-image-resize')
          ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 120);
    },
    [setActiveTab]
  );

  /**
   * Close the modal and open the picture chooser. SYM-6096: when no crop of
   * this picture can work, the crop tool is the wrong place to send anyone.
   * (SYM-6135 will sharpen such a picture through fal inside the request
   * before the guard runs, so this becomes the way out when even that fails.)
   */
  const changePictureForBtl = useCallback(
    (choices: BtlChoices) => {
      setBtlChoicesDraft(choices);
      setBtlOpen(false);
      setActiveTab(AdEditorSideBarTabs.VISUAL_OPTIONS);
      setUploadMode(true);
    },
    [setActiveTab, setUploadMode]
  );

  /**
   * SYM-6353. The ad and picture the Bring to Life preview is drawn from: the
   * same picture, crop and ad the clip itself will be made from, so the
   * preview shows the clip's own frame.
   */
  const btlPreviewSource = useMemo(() => {
    if (!activeVacancy || !currentVariant?.path?.path) return null;
    const crop = getBtlCrop(currentVariant);
    return {
      vacancy_id: activeVacancy,
      ...(typeof currentVariant.id === 'number'
        ? { ad_variant_id: currentVariant.id }
        : {}),
      format: currentVariant.placement ?? 'reels',
      source_image_url: currentVariant.path.path,
      source_resource_id: currentVariant.path.id,
      ...(crop ? { crop } : {}),
    };
  }, [activeVacancy, currentVariant]);

  const handleBtlSubmit = useCallback(
    async (choices: BtlChoices) => {
      const { instructions } = choices;
      if (!activeVacancy || !currentVariant?.path?.path) return;
      setBtlSubmitError(null);
      setBtlWordErrors({});
      setBtlStage('generating');
      setBtlSharpening(
        bringToLifeWillSharpen(
          currentVariant.path.width,
          currentVariant.path.height,
          currentVariant.placement
        )
      );
      try {
        // Forward the user's focus rectangle + the source resource id. The BE
        // crops to the focus and fits the placement's output aspect.
        const crop = getBtlCrop(currentVariant);
        const result = await generateVideoFal({
          vacancy_id: activeVacancy,
          format: currentVariant?.placement ?? 'reels',
          video: {
            provider: 'fal',
            source_image_url: currentVariant.path.path,
            source_resource_id: currentVariant.path.id,
            ...(crop ? { crop } : {}),
            prompt: instructions || undefined,
            ...btlOverlayFields(currentVariant.id, choices),
          },
        });
        setBtlGenerationId(result.ai_generation_id ?? null);
        setVideoLocked('fal', true); // SYM-5391: this tab holds the lock now; tell the others
        // Accepted, so the choices have done their job: the next fresh open
        // starts from the defaults and the ad's own words.
        setBtlChoicesDraft(null);
      } catch (err: any) {
        const status = err?.response?.status;
        const serverMessage: string | undefined =
          err?.response?.data?.message ?? err?.response?.data?.error;
        // A 409 is the canonical signal, but also accept any 4xx whose body
        // reads like a concurrency conflict — covers BE variants we can't
        // fully pin down from the FE.
        const looksLikeLock =
          status === 409 ||
          (!!serverMessage &&
            /already|in progress|locked|pending/i.test(serverMessage));

        // SYM-6073. The server refuses a picture fal cannot animate, and its
        // refusal is written for the customer: it names the size, the minimum,
        // and how much of the picture the crop has to cover. Show it where they
        // can act on it, on the input screen, rather than dropping them on the
        // "something went wrong, try adjusting your instructions" screen — the
        // instructions have nothing to do with it, and that advice sent people
        // round a 25-second loop they could not win.
        //
        // Read from THIS key rather than the response's `message`, so only our
        // own refusal is put in front of a customer. Any other validation
        // failure is a bug in what we sent and reads like one.
        const refusal =
          status === 422 ? readBtlRefusal(err?.response?.data?.errors) : null;
        // SYM-6353. All three words empty. The screen stops this before it is
        // sent, so this is the server agreeing with it; the customer is put
        // back on the screen with the server's own sentence.
        const wordRefusal =
          status === 422 ? readWordRefusal(err?.response?.data?.errors) : null;

        if (wordRefusal) {
          // Back on the screen, the server's sentence under the word it is
          // about, and the one about all three under the button.
          setBtlStage('input');
          setBtlWordErrors(wordRefusal.words);
          setBtlSubmitError(wordRefusal.all);
        } else if (looksLikeLock) {
          setBtlStage('input');
          setBtlSubmitError(
            'A video is already being generated for this vacancy. Please wait a moment and try again.'
          );
        } else if (refusal) {
          // Its own screen, not a line under the box: this cannot be fixed by
          // editing what they typed, so the screen that says so is the one
          // that takes them to the crop tool, or to a different picture.
          setBtlRefusal(refusal);
          // SYM-6096. The crop tool colours its box by these until Bring to
          // Life is opened again or the picture changes.
          setBtlCropMinimum(
            refusal.minimum && currentVariant.path?.path
              ? { ...refusal.minimum, path: currentVariant.path.path }
              : null
          );
          setBtlChoicesDraft(choices);
          setBtlStage('picture-too-small');
        } else {
          setBtlStage('error');
        }
      } finally {
        setBtlSharpening(false);
      }
    },
    [activeVacancy, currentVariant]
  );

  // ── Shared mutations (copy is used by both T1 and legacy) ────────────────
  const videoRequestAborterController = new AbortController();

  const handleError = (error: string) => {
    if (error.includes('400')) {
      setVideoGenError([
        'Our video generator needs more images in order to create a video.',
        'Make sure you uploaded at least 3 visuals, either linked to your brand or as ad variants for this campaign.',
      ]);
    } else {
      setVideoGenError([
        'Something went wrong while processing your request. Please note this is a beta feature.',
      ]);
    }
  };

  const [copyVideoRequest, { loading: loadingCopy }] = useMutation<
    { video: CustomerResource },
    { vacancyId: number; format: string; threadId: string }
  >(COPY_VIDEO, {
    abortController: videoRequestAborterController,
    onError: (e: ApolloError) => handleError(e.message),
  });

  // ── Track 1 entry point (Creatomate wizard) ──────────────────────────────
  //
  // SYM-6197. `pickedPictureIds` are pictures the customer named at the
  // refusal, from their own media library. The campaign's pictures are what a
  // video is normally built from, and these are added in front of them rather
  // than instead of them, so nobody has to make an ad per picture to get a
  // video.
  const videoGenT1 = useCallback(
    async (pickedPictureIds?: number[]) => {
      fireEvent('video_gen');
      if (!activeVacancy) return;

      // SYM-6185. Decided BEFORE anything opens: a Bring to Life clip has no
      // slides, so the wizard never opens from it. The toolbar greys the
      // sparkle out; this is the same rule for any other caller.
      const entry = wizardEntryFor({
        adIsVideo: currentVariant?.creative_type === AdCreativeType.VIDEO,
        adIsBringToLife: carriesBringToLifeClip(currentVariant, variants),
        resourceId: currentVariant?.path?.id,
        pickedPictureIds,
      });
      if (entry.kind === 'bring-to-life') {
        addToast({
          type: ToastTypes.INFO,
          title: 'This video cannot be edited',
          description: CANNOT_EDIT_BRING_TO_LIFE,
        });
        return;
      }

      setPreviewLoading(true);
      setWizardOpen(true);
      setVideoGenError(null);
      setGenerationId(null);
      setIsRendering(false);

      try {
        // SYM-6185. On an ad that carries a video this opens THAT video again;
        // anywhere else it asks for a fresh proposal, as before.
        const config =
          entry.kind === 'reopen'
            ? await reopenVideo(entry.resourceId)
            : await fetchPreviewConfig({
                vacancy_id: activeVacancy,
                format: currentVariant?.placement ?? 'reels',
                video: { provider: 'creatomate' },
                // Array.isArray, not a length check: this entry point is also
                // wired straight to a button, so the first argument can be a
                // click event.
                ...(Array.isArray(pickedPictureIds) && pickedPictureIds.length
                  ? { image_resource_ids: pickedPictureIds }
                  : {}),
              });

        setDraftId(config.draft_id);
        setPreviewConfig(config.config);
        // SYM-6196. The limits the server holds this video to, passed to the
        // wizard so it holds the customer to the same ones.
        setMaxSlideCharacters(config.limits?.max_slide_characters ?? null);
        setStylePaces(config.limits?.styles ?? null);
        setPreviewPayload(config.preview_payload ?? null);
        setPreviewPlan(config.preview_plan ?? null);
        setInitialStyle(config.config?.render_style ?? DEFAULT_TEMPLATE_STYLE);
        setInitialLogoPosition(logoPositionFrom(config.config?.logo_position));
        // SYM-6215. The scenes come from the composition; which lengths the
        // customer set by hand is stored on the draft's own slides.
        // SYM-6272. ...and so are the crops it already holds. The wizard names
        // every slide's crop on each update, so it has to start out knowing them.
        setInitialScenes(
          withWhatTheDraftHolds(
            parsePreviewPayload(config.preview_payload),
            config.config?.slides
          )
        );
      } catch (err) {
        const status = (err as any)?.response?.status;
        const serverMessage = (err as any)?.response?.data?.message;
        setVideoGenError([
          // SYM-6185. 404 = nothing behind this file (uploaded, or made before
          // drafts were kept). Say what to do instead; there is no second
          // button for a fresh video, by decision.
          status === 404
            ? CANNOT_REOPEN
            : serverMessage ??
              'Could not load video preview. Please try again.',
        ]);
      } finally {
        setPreviewLoading(false);
      }
    },
    [
      activeVacancy,
      currentVariant?.placement,
      currentVariant?.creative_type,
      currentVariant?.bring_to_life_resource_id,
      variants,
      currentVariant?.path?.id,
      addToast,
    ]
  );

  // ── Refresh preview via /update — invoked when user navigates to Step 3 ──
  // Only fires when edits exist (vs initial/last-synced state). Pushes the new
  // preview_payload to state so the Preview SDK re-renders with user's edits
  // applied. Also updates initialScenes/initialStyle so subsequent edit
  // detection is relative to the now-synced state.
  const [isRefreshingPreview, setIsRefreshingPreview] = useState(false);

  const refreshPreview = useCallback(
    async (
      scenes: VideoScene[],
      style: VideoTemplateStyle,
      choices: VideoWideChoices = {}
    ) => {
      if (!draftId || !previewConfig) return;
      const hasEdits = detectScenesEdited(
        scenes,
        initialScenes,
        style,
        initialStyle,
        choices.logoPosition ?? initialLogoPosition,
        initialLogoPosition
      );
      if (!hasEdits) return;

      setIsRefreshingPreview(true);
      try {
        const response = await updateVideo(
          buildUpdatePayload(draftId, scenes, style, previewConfig, choices)
        );
        if (response.preview_payload) {
          setPreviewPayload(response.preview_payload);
        }
        // SYM-6206. Every edit gets a fresh plan, which is what keeps the
        // preview honest while the customer is typing. Guarded like the line
        // above: a server that sends nothing must not wipe what we have.
        if (response.preview_plan) {
          setPreviewPlan(response.preview_plan);
        }
        // Sync baseline so we don't re-fire /update for the same edits
        setInitialScenes(scenes);
        setInitialStyle(style);
        if (choices.logoPosition) setInitialLogoPosition(choices.logoPosition);
      } catch (err) {
        // Swallow — navigating to Step 3 with stale preview is better UX than
        // blocking the user on a refresh failure. Final render on Accept still
        // fires /update so edits are not lost.
        // eslint-disable-next-line no-console
        console.error('Failed to refresh preview:', err);
      } finally {
        setIsRefreshingPreview(false);
      }
    },
    [draftId, previewConfig, initialScenes, initialStyle, initialLogoPosition]
  );

  // ── Accept: fire /update (edits) or /generate (no edits) ─────────────────
  const handleAccept = useCallback(
    async (
      scenes: VideoScene[],
      style: VideoTemplateStyle,
      choices: VideoWideChoices = {}
    ) => {
      if (!draftId) return;

      const scenesEdited = detectScenesEdited(
        scenes,
        initialScenes,
        style,
        initialStyle,
        choices.logoPosition ?? initialLogoPosition,
        initialLogoPosition
      );

      try {
        setIsRendering(true);

        if (scenesEdited && previewConfig) {
          await updateVideo(
            buildUpdatePayload(draftId, scenes, style, previewConfig, choices)
          );
        }

        const result = await generateVideo({ draft_id: draftId });
        if (result.path) {
          handlePollingCompleted(result as unknown as RenderedResource);
        } else if (result.ai_generation_id) {
          setGenerationId(result.ai_generation_id ?? null);
        }
      } catch (err) {
        setIsRendering(false);
        const serverMessage = (err as any)?.response?.data?.message;
        setVideoGenError([
          serverMessage ?? 'Could not start video rendering. Please try again.',
        ]);
      }
    },
    [draftId, previewConfig, initialScenes, initialStyle, initialLogoPosition]
  );

  // ── Copy video (uses legacy COPY_VIDEO mutation; thread_id-based) ────────
  const copyVideo = useCallback(async () => {
    if (!currentVariant?.placement) return;

    const activeThread = currentVariant?.path?.thread_id ?? '';
    if (!activeThread) return;

    const newPlacements =
      currentVariant!.placement === AdPlacement.FEED
        ? [AdPlacement.REELS, AdPlacement.STORIES]
        : [AdPlacement.FEED];

    setLegacyModalOpen(true);

    for (const newPlacement of newPlacements) {
      const { data, errors } = await copyVideoRequest({
        variables: {
          vacancyId: activeVacancy!,
          format: newPlacement,
          threadId: activeThread,
        },
      });
      if (!!errors) return;

      const referenceVariant =
        variants.find(({ placement: p }) => p === newPlacement) ?? variants[0];
      if (!referenceVariant) continue;

      const bannerTitle =
        currentVacancy?.brand?.default_banner_title ?? 'WANTED';
      addVariant({
        ...referenceVariant,
        id: undefined,
        path: data!.video,
        ...(newPlacement === AdPlacement.REELS && { logo: null }),
        ...([AdPlacement.STORIES, AdPlacement.REELS].includes(newPlacement) && {
          description: '',
          ...(newPlacement === AdPlacement.REELS && {
            text: `${bannerTitle}: ${currentVacancy?.title} ${
              currentVacancy?.location ? `in ${currentVacancy.location}` : ''
            }`,
          }),
        }),
        svg: null,
        data_url: undefined,
        config: undefined,
        crop: null,
        isDeleted: false,
        isDirty: true,
        placement: newPlacement,
        creative_type: AdCreativeType.VIDEO,
        uuid: uniqueId(),
      });
    }

    setLegacyModalOpen(false);
  }, [
    activeVacancy,
    copyVideoRequest,
    currentVariant,
    currentVacancy,
    variants,
    addVariant,
  ]);

  // ──────────────────────────────────────────────────────────────────────────
  // LEGACY: ai-video flow (pre-T1, pre-Creatomate)
  // Resurrected from origin/master so customers in the ai-video audience but
  // outside the video-track-1-scenario rollout keep the OpenAI-threads flow.
  // Drop this entire block — state, mutations, callbacks, and the JSX content
  // inside the GenerateVideoModal below — once ai-video is retired in PostHog.
  // ──────────────────────────────────────────────────────────────────────────
  const [activeThread, setActiveThread] = useState<string>('');

  const [generateVideoRequest, { loading: loadingGenerate }] = useMutation<
    { video: CustomerResource },
    { vacancyId: number; format: string }
  >(GENERATE_VIDEO, {
    abortController: videoRequestAborterController,
    onError: (e: ApolloError) => handleError(e.message),
  });

  const [updateVideoRequest, { loading: loadingUpdate }] = useMutation<
    { video: CustomerResource },
    {
      vacancyId: number;
      format: string;
      feedback: string;
      threadId: string;
      oldVideoId: number;
    }
  >(UPDATE_VIDEO, {
    abortController: videoRequestAborterController,
    onError: (e: ApolloError) => handleError(e.message),
  });

  const legacyLoading = loadingCopy || loadingUpdate || loadingGenerate;

  const generateVideoHandler = useCallback(async () => {
    setLegacyModalOpen(true);
    const { data, errors } = await generateVideoRequest({
      variables: {
        vacancyId: activeVacancy!,
        format: currentVariant!.placement,
      },
    });
    if (!!errors) return;
    setLegacyModalOpen(false);
    updateCurrentVariant('path', data!.video, true);
    setVideoGenError(null);
  }, [
    activeVacancy,
    currentVariant,
    generateVideoRequest,
    updateCurrentVariant,
  ]);

  const videoGenLegacy = useCallback(async () => {
    fireEvent('video_gen');
    // Existing thread + variant → open feedback flow; otherwise generate fresh.
    if (!!activeThread && !!currentVariant?.path) {
      setLegacyModalOpen(true);
    } else {
      await generateVideoHandler();
    }
  }, [activeThread, currentVariant, generateVideoHandler]);

  const videoFeedbackHandler = useCallback(
    async (feedback: string) => {
      setLegacyModalOpen(true);
      const { data, errors } = await updateVideoRequest({
        variables: {
          vacancyId: activeVacancy!,
          format: currentVariant!.placement,
          feedback,
          threadId: activeThread,
          oldVideoId: currentVariant!.path!.id,
        },
      });
      if (!!errors) return;
      setLegacyModalOpen(false);
      updateCurrentVariant('path', data!.video, true);
      setVideoGenError(null);
    },
    [
      activeVacancy,
      currentVariant,
      activeThread,
      updateVideoRequest,
      updateCurrentVariant,
    ]
  );

  const abortVideoRequest = useCallback(() => {
    videoRequestAborterController.abort();
    setLegacyModalOpen(false);
    setVideoGenError(null);
  }, [videoRequestAborterController]);

  const legacyRetry = useCallback(() => {
    setVideoGenError(null);
    if (!activeThread || !currentVariant?.path) {
      generateVideoHandler();
    }
  }, [activeThread, currentVariant, generateVideoHandler]);

  // Track activeThread for the legacy feedback path.
  useEffect(() => {
    setActiveThread(currentVariant?.path?.thread_id ?? '');
  }, [currentVariant]);

  // ── Route the public videoGen based on the T1 flag ───────────────────────
  // Track 1 audience → Creatomate wizard. Otherwise (ai-video fallback) →
  // legacy OpenAI-threads flow. LEGACY: collapse this back to `videoGen = videoGenT1`
  // when ai-video is retired.
  const exposedVideoGen = hasT1 ? videoGenT1 : videoGenLegacy;

  // ── Memoized provider value ──────────────────────────────────────────────
  // Provider re-renders on every polling tick (progress etc). Without memo,
  // every consumer re-renders each tick, which surfaces a latent useApiList
  // instability in AdBuilder and traps React in a Maximum-update-depth loop.
  // SYM-5391. Track 1 holds the creatomate lock for as long as it renders.
  const wasRendering = useRef(false);
  useEffect(() => {
    if (isRendering === wasRendering.current) return;
    wasRendering.current = isRendering;
    setVideoLocked('creatomate', isRendering);
  }, [isRendering, setVideoLocked]);

  const isAnyVideoRendering =
    isRendering || btlStage === 'generating' || legacyLoading;
  const contextValue = useMemo(
    () => ({
      videoGen: exposedVideoGen,
      copyVideo,
      bringToLife,
      isAnyVideoRendering,
      videoLocks,
    }),
    [exposedVideoGen, copyVideo, bringToLife, isAnyVideoRendering, videoLocks]
  );

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      {/* Legacy modal — also doubles as the copy-video loader */}
      <GenerateVideoModal
        show={legacyModalOpen}
        hideCloseButton={legacyLoading}
        onClose={abortVideoRequest}
      >
        <>
          {videoGenError ? (
            <div tw="flex flex-col gap-2 items-center mb-2">
              {videoGenError.map((e, idx) => (
                <p key={`videogen-error-${idx}`} tw="break-words">
                  {e}
                </p>
              ))}
              <p>If the problem persists, please let us know.</p>
              <Button customStyle={tw`mt-4`} onClick={legacyRetry}>
                Retry
              </Button>
            </div>
          ) : loadingCopy && !loadingGenerate && !loadingUpdate ? (
            <div tw="flex flex-col gap-2 items-center mb-2">
              <SpinningSymplLoader
                messages={['Copying video to other formats...']}
                delay={3000}
                loading={true}
              />
            </div>
          ) : !!activeThread && !legacyLoading ? (
            <FeedbackModal feedbackFunc={videoFeedbackHandler} />
          ) : (
            <AbortModal
              abortFunc={abortVideoRequest}
              isFeedbackRun={!!activeThread}
            />
          )}
        </>
      </GenerateVideoModal>

      {/* Track 1 wizard */}
      <VideoPreviewModal
        show={wizardOpen}
        compact={!previewLoading && !!videoGenError}
        onClose={() => {
          setIsRendering(false);
          setWizardOpen(false);
        }}
      >
        {previewLoading ? (
          <div tw="flex flex-col items-center justify-center h-full gap-4">
            <SpinningSymplLoader
              messages={[
                'Selecting the best images for your video...',
                'Generating your video preview...',
              ]}
              delay={2500}
              loading={true}
            />
            <Footnote mt={1} isCenter>
              This usually takes a few seconds.
            </Footnote>
          </div>
        ) : videoGenError ? (
          <div tw="flex flex-col gap-3 items-center text-center px-6 py-8">
            {pictureRefusal ? (
              <>
                <p tw="text-gray-700">
                  {describePictureRefusal(pictureRefusal)}
                </p>
                {/*
                  SYM-6197. The way out is the customer's own media library,
                  not the ad uploader.

                  A campaign has no picture pool: a video is built from the
                  pictures on the campaign's ads plus the brand's default
                  creatives. Sending someone to the ad uploader made them
                  create an ad per picture, one at a time, and the count did
                  not move until those ads were saved. Picking from the library
                  needs none of that: the pictures are named on the request
                  that starts the video.
                */}
                <Button
                  customStyle={tw`mt-2`}
                  onClick={() => setPicturePickerOpen(true)}
                >
                  Choose pictures for this video
                </Button>
                <Button
                  variant="inverted"
                  onClick={() => {
                    setVideoGenError(null);
                    setWizardOpen(false);
                    // SYM-6197. Walking away from the video should not leave
                    // the blank ad behind that was only made to hold it. Only
                    // a leftover is cleared; see emptyVariant.ts.
                    if (isEmptyUnsavedVariant(currentVariant)) {
                      deleteCurrentVariant();
                    }
                  }}
                >
                  Not now
                </Button>
              </>
            ) : (
              <>
                {videoGenError.map((e, idx) => (
                  <p key={idx} tw="break-words text-gray-700">
                    {e}
                  </p>
                ))}
                {/* A video with nothing behind it stays that way: trying
                    again would only ask the same question. */}
                {!videoGenError.includes(CANNOT_REOPEN) && (
                  <Button
                    customStyle={tw`mt-2`}
                    onClick={() => {
                      setVideoGenError(null);
                      videoGenT1();
                    }}
                  >
                    Retry
                  </Button>
                )}
              </>
            )}
          </div>
        ) : (
          <VideoWizard
            key={draftId ?? 'wizard'}
            initialScenes={initialScenes}
            initialStyle={initialStyle}
            initialLogoPosition={initialLogoPosition}
            previewPayload={previewPayload}
            previewPlan={previewPlan}
            /*
              SYM-6308 (Steven, 22 Sep 2026). Open on Preview, not on Scenes.

              SYM-6220 opened on Scenes (initialStep 2) because that was where
              the work was and the video was on screen anyway - it sat beside
              every step. It no longer does: it is on the Preview step and
              nowhere else. Landing on Scenes would mean a customer never sees
              the video they asked for until they press something, so the
              wizard opens where the video is, and Style and Scenes become
              where you go to change it. The left pane there says so.

              The number itself is the wizard's own (WIZARD_OPENS_ON), so the
              step the wizard opens on is stated once, where the steps are.

              Nothing is missed by skipping the rebuild the step row does on the
              way to Preview: the draft this modal was opened with came from
              /preview-config, so the composition on screen is already the
              current one.
            */
            initialStep={WIZARD_OPENS_ON}
            draftId={draftId ?? undefined}
            isRendering={isRendering}
            renderProgress={progress}
            maxSlideCharacters={maxSlideCharacters}
            stylePaces={stylePaces}
            styleExamples={styleExamples}
            onRetryStyleExamples={retryStyleExamples}
            onRender={handleAccept}
            onRefreshPreview={refreshPreview}
            isRefreshingPreview={isRefreshingPreview}
            onCancel={() => {
              setIsRendering(false);
              setWizardOpen(false);
            }}
            vacancyId={activeVacancy}
          />
        )}
      </VideoPreviewModal>

      {/*
        SYM-6197. Picking the pictures a video is built from, at the refusal.
        Several at once, from the customer's own library, and the uploader in
        that same screen takes new ones.
      */}
      <ResourcesModal
        show={picturePickerOpen}
        mode="image"
        onClose={() => setPicturePickerOpen(false)}
        onSubmit={(resources: CustomerResource[]) => {
          setPicturePickerOpen(false);
          const ids = resources.map(({ id }) => id).filter(Boolean);
          if (!ids.length) return;
          videoGenT1(ids);
        }}
      />

      {/* Track 2 — Bring to life modal */}
      <BringToLifeModal
        key={btlKey}
        show={btlOpen}
        progress={btlProgress}
        stage={btlStage}
        submitError={btlSubmitError}
        refusalReason={btlRefusal?.reason ?? null}
        cropCanFixIt={cropCanFixIt(btlRefusal)}
        sharpeningFirst={btlSharpening}
        initialChoices={btlChoicesDraft}
        previewSource={btlPreviewSource}
        wordErrors={btlWordErrors}
        onSubmit={handleBtlSubmit}
        onRetry={handleBtlSubmit}
        onAdjustCrop={adjustCropForBtl}
        onChangePicture={changePictureForBtl}
        onClose={() => setBtlOpen(false)}
      />

      <VideoGenContext.Provider value={contextValue}>
        {children}
      </VideoGenContext.Provider>
    </>
  );
};

// ──────────────────────────────────────────────────────────────────────────
// LEGACY sub-components — used by the legacy ai-video modal above.
// Drop both once ai-video is retired.
// ──────────────────────────────────────────────────────────────────────────

interface IFeedbackModal {
  feedbackFunc: (feedback: string) => void;
}

interface IFeedbackForm {
  feedback: string;
}

const FeedbackModal: React.FC<IFeedbackModal> = ({ feedbackFunc }) => {
  const { handleSubmit, control } = useForm<IFeedbackForm>({
    defaultValues: { feedback: '' },
  });
  const submitFunc = async ({ feedback }: IFeedbackForm) => {
    feedbackFunc(feedback);
  };

  return (
    <form
      tw="flex flex-col items-center gap-4"
      onSubmit={handleSubmit(submitFunc)}
    >
      <h2>Give feedback to the generated video</h2>
      <Controller
        control={control}
        name="feedback"
        render={({ onChange, onBlur, value }) => (
          <Input
            tw="w-[30vw] max-w-[40ch]"
            textarea
            autoGrow
            placeholder="e.g.: Switch the image in the first and second slide"
            onChange={onChange}
            onBlur={onBlur}
            value={value}
          />
        )}
      />
      <Button type="submit">Regenerate</Button>
    </form>
  );
};

interface IAbortModal {
  abortFunc: () => void;
  isFeedbackRun: boolean;
}

const AbortModal: React.FC<IAbortModal> = ({ abortFunc, isFeedbackRun }) => {
  return (
    <div tw="flex flex-col gap-2 items-center mb-2">
      <SpinningSymplLoader
        messages={[
          'Starting up the robots 🤖',
          isFeedbackRun
            ? 'Handling your feedback 🤝'
            : 'Writing the script for your video 📝',
          'Gathering visuals & texts',
          'Started generating the video 📸',
          'Take some time and enjoy a coffee ☕',
          'Tip: You can make changes to the video by \nclicking the ✨ icon in the toolbar',
          'Almost done! 🚀',
          "We're finishing up... ✅",
        ]}
        delay={7000}
        loading={true}
      />
      <Button customStyle={tw`mx-auto inline-block`} onClick={abortFunc}>
        Cancel
      </Button>
      <Footnote mt={1} isCenter>
        Please note this is a beta feature.
        <br /> We appreciate your feedback via the button on the right.
      </Footnote>
    </div>
  );
};

export default VideoGenProvider;
